/**
 * Wave 3/4 — bounce to Dokončit ověření when `/me.mustCompleteVerification` and path
 * is outside the allowlist (complete-verification / add-phone). Logout stays allowed
 * via session-end (session null → no gate).
 */
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  COMPLETE_VERIFICATION_PATH,
  evaluateIdentityPipeline,
} from '../../../navigation/identityPipeline.js';
import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';
import { useAccountMe } from '../accountApi.js';

interface RequireContactVerificationCompleteProps {
  readonly children: ReactNode;
}

export function RequireContactVerificationComplete({
  children,
}: RequireContactVerificationCompleteProps): ReactNode {
  const { session, sessionHydrated } = useCustomerSession();
  const location = useLocation();
  const me = useAccountMe(session !== null);

  if (!sessionHydrated || session === null) {
    return children;
  }

  // Enforce only when `/me` has settled with the flag — avoid flash redirects while loading.
  if (me.data === undefined || me.isLoading) {
    return children;
  }

  const decision = evaluateIdentityPipeline({
    routeMatched: true,
    entitlementsLoading: false,
    entitlementsGranted: true,
    entitlementMode: 'inline-unavailable',
    sessionHydrated: true,
    sessionPresent: true,
    accountMeReady: true,
    accountMeValid: me.data.customerId === session.customerId,
    mustCompleteVerification: me.data.mustCompleteVerification === true,
    pathname: location.pathname,
  });

  if (decision.redirectToCompleteVerification === true) {
    return <Navigate to={COMPLETE_VERIFICATION_PATH} replace />;
  }

  return children;
}
